import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import fontBold from "../../assets/fonts/NotoSansTamil-Bold.ttf";
import fontRegular from "../../assets/fonts/NotoSansTamil-Regular.ttf";

// Register fonts
Font.register({
  family: "NotoSansTamil",
  fonts: [
    { src: fontRegular, fontWeight: "normal" },
    { src: fontBold, fontWeight: "bold" },
  ],
});

// ... imports

const styles = StyleSheet.create({
  page: {
    padding: 40,
    backgroundColor: "#ffffff",
    fontFamily: "NotoSansTamil",
  },
  headerContainer: {
    marginBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: "#eeeeee",
    paddingBottom: 10,
  },
  header: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#1a237e", // Deep Blue
    textAlign: "center",
  },
  subHeaderContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 15,
  },
  subHeaderText: {
    fontSize: 10,
    color: "#555555",
  },
  table: {
    display: "table",
    width: "100%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#e0e0e0",
    borderRadius: 5,
    overflow: "hidden", // For rounded corners
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    minHeight: 25,
    alignItems: "center",
  },
  tableCol: {
    padding: 8,
  },
  tableCell: {
    fontSize: 10,
    color: "#333333",
  },
  tableHeader: {
    backgroundColor: "#1a237e", // Deep Blue header
  },
  tableHeaderCell: {
    color: "#ffffff",
    fontWeight: "bold",
    fontSize: 10,
  },
  totalRow: {
    backgroundColor: "#f5f5f5",
    borderTopWidth: 2,
    borderTopColor: "#1a237e",
  },
  creditColor: { color: "#d32f2f" },
  debitColor: { color: "#2e7d32" },
});

const StaffAdvancePDF = ({ data, staffName, fromDate, toDate }) => {
  const totalCredit = data.reduce(
    (acc, item) =>
      item.type === "credit" ? acc + parseFloat(item.amount) : acc,
    0,
  );
  const totalDebit = data.reduce(
    (acc, item) =>
      item.type === "debit" ? acc + parseFloat(item.amount) : acc,
    0,
  );
  const netBalance = totalDebit - totalCredit;

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const colWidths = {
    date: "25%",
    credit: "35%",
    debit: "40%",
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerContainer}>
          <Text style={styles.header}>STAFF ADVANCE STATEMENT</Text>
        </View>

        <View style={styles.subHeaderContainer}>
          <Text style={styles.subHeaderText}>Staff Name: {staffName}</Text>
          {(fromDate || toDate) && (
            <Text style={styles.subHeaderText}>
              Period: {formatDate(fromDate)} to {formatDate(toDate)}
            </Text>
          )}
          {!(fromDate || toDate) && (
            <Text style={styles.subHeaderText}>
              Date: {new Date().toLocaleDateString("en-GB").replace(/\//g, "-")}
            </Text>
          )}
        </View>

        <View style={styles.table}>
          {/* Table Header */}
          <View style={[styles.tableRow, styles.tableHeader]} fixed>
            <View style={[styles.tableCol, { width: colWidths.date }]}>
              <Text style={styles.tableHeaderCell}>Date</Text>
            </View>
            <View style={[styles.tableCol, { width: colWidths.credit }]}>
              <Text style={styles.tableHeaderCell}>Credit (Deduction)</Text>
            </View>
            <View style={[styles.tableCol, { width: colWidths.debit }]}>
              <Text style={styles.tableHeaderCell}>Debit (Advance)</Text>
            </View>
          </View>

          {/* Table Body */}
          {data && data.length > 0 ? (
            data.map((item, index) => (
              <View style={styles.tableRow} key={index} wrap={false}>
                <View style={[styles.tableCol, { width: colWidths.date }]}>
                  <Text style={styles.tableCell}>
                    {formatDate(item.entry_date)}
                  </Text>
                </View>
                <View style={[styles.tableCol, { width: colWidths.credit }]}>
                  <Text
                    style={[
                      styles.tableCell,
                      item.type === "credit" ? styles.creditColor : {},
                    ]}
                  >
                    {item.type === "credit"
                      ? `-${parseFloat(item.amount).toFixed(2)}`
                      : "-"}
                  </Text>
                </View>
                <View style={[styles.tableCol, { width: colWidths.debit }]}>
                  <Text
                    style={[
                      styles.tableCell,
                      item.type === "debit" ? styles.debitColor : {},
                    ]}
                  >
                    {item.type === "debit"
                      ? `+${parseFloat(item.amount).toFixed(2)}`
                      : "-"}
                  </Text>
                </View>
              </View>
            ))
          ) : (
            <View style={styles.tableRow}>
              <View style={[styles.tableCol, { width: "100%" }]}>
                <Text
                  style={{
                    ...styles.tableCell,
                    textAlign: "center",
                    fontStyle: "italic",
                  }}
                >
                  No Records Found
                </Text>
              </View>
            </View>
          )}

          {/* Total Row */}
          <View style={[styles.tableRow, styles.totalRow]}>
            <View style={[styles.tableCol, { width: colWidths.date }]}>
              <Text style={styles.totalText}>Total</Text>
            </View>
            <View style={[styles.tableCol, { width: colWidths.credit }]}>
              <Text style={[styles.totalText, styles.creditColor]}>
                {totalCredit.toFixed(2)}
              </Text>
            </View>
            <View style={[styles.tableCol, { width: colWidths.debit }]}>
              <Text style={[styles.totalText, styles.debitColor]}>
                {totalDebit.toFixed(2)}
              </Text>
            </View>
          </View>

          {/* Net Balance Row */}
          <View
            style={[
              styles.tableRow,
              { borderTopWidth: 2, borderTopColor: "#000" },
            ]}
          >
            <View style={[styles.tableCol, { width: "60%" }]}>
              <Text
                style={{ fontSize: 12, fontWeight: "bold", textAlign: "right" }}
              >
                Net Balance Due:
              </Text>
            </View>
            <View style={[styles.tableCol, { width: "40%" }]}>
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: "bold",
                  textAlign: "center",
                }}
              >
                {netBalance.toFixed(2)}
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default StaffAdvancePDF;
